import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { SITE_URL, faqSchema, offerSchema } from "@/lib/seo";
import { HOME } from "@/constants/testIds";

const features = [
  "Custom design, never a template",
  "Mobile-first, responsive build",
  "Hosting included, securely managed",
  "Lead capture & contact setup",
  "SEO foundations from day one",
  "Ongoing support & refinements",
];

const faqs = [
  { q: "Do I really pay nothing upfront?", a: "Yes. The atelier designs and builds your website at no cost. You only begin the £99 monthly retainer once your site is live and you are happy with it." },
  { q: "What does the £99 a month cover?", a: "Premium hosting, security, daily backups, minor content updates, ongoing SEO refinements, and direct support from the atelier. A quiet, dependable studio on retainer." },
  { q: "Is there a contract?", a: "A light six-month minimum on the monthly retainer, which covers the time we invest in your launch. After that, cancel any time with 30 days' written notice." },
  { q: "How long does it take?", a: "Every website is ready in less than a week. We move quickly, with care, so you are online and taking enquiries while competitors are still waiting on a quote." },
  { q: "Do you redesign existing sites?", a: "Yes. We rebuild existing sites under exactly the same terms, free design and build, £99 a month thereafter. Send us your current URL and we will take it from there." },
];

export default function Pricing() {
  const crumbs = [
    { name: "Home", url: `${SITE_URL}/` },
    { name: "Pricing", url: `${SITE_URL}/pricing` },
  ];
  return (
    <main className="pt-32 sm:pt-40 bg-lak-bg">
      <Seo
        title="Pricing · Website Built Free, £99 Monthly Retainer"
        description="One transparent plan. Your website is designed and built free of charge. £99 a month covers hosting, support, security and ongoing SEO."
        keywords="free web design UK, web design monthly retainer, no upfront cost website, web design with hosting included"
        breadcrumbs={crumbs}
        schemas={[offerSchema(), faqSchema(faqs)]}
      />
      <section className="lak-section pb-20 sm:pb-32">
        <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14">
          <Breadcrumbs items={crumbs} className="mb-8" />
          <Reveal><span className="eyebrow">Pricing · The offer</span></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-8 font-serif text-6xl sm:text-7xl lg:text-9xl font-light tracking-tight leading-[0.96] max-w-5xl">
              Considered web design,<br />
              <em className="italic text-lak-sage">composed at the atelier.</em>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-10 max-w-2xl text-lg text-lak-soft">
              One transparent plan. Every detail of your website, designed with intention and built to feel unmistakably yours.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid={HOME.pricingSection} className="lak-section pb-32">
        <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14">
          <Reveal>
            <div className="max-w-3xl mx-auto bg-lak-secondary border border-black/10 p-10 sm:p-16 relative">
              <div className="absolute -top-4 left-10 sm:left-16 bg-lak-highlight text-lak-text px-4 py-1 eyebrow">
                LAK Launch
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
                <div>
                  <p className="font-serif text-7xl sm:text-8xl font-light tracking-tighter leading-none italic text-lak-sage">Free</p>
                  <p className="eyebrow mt-3">Website, designed &amp; built</p>
                  <div className="my-8 h-px bg-black/10" />
                  <p className="font-serif text-5xl sm:text-6xl font-light tracking-tighter leading-none">&pound;99<span className="text-2xl text-lak-soft">/mo</span></p>
                  <p className="eyebrow mt-3">Monthly retainer, hosting &amp; SEO</p>
                </div>
                <div>
                  <ul className="space-y-4">
                    {features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-base">
                        <Check size={18} className="text-lak-sage mt-1 shrink-0" strokeWidth={1.25} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact" data-testid={HOME.pricingCta} className="mt-10 inline-flex items-center gap-3 px-8 py-4 btn-sage text-[11px] tracking-[0.28em] uppercase">
                    Start Your Project <ArrowUpRight size={16} strokeWidth={1.25} />
                  </Link>
                  <p className="mt-5 text-xs text-lak-soft">No long contracts. Cancel any time after the first 6 months.</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="lak-section py-28 sm:py-40 bg-lak-secondary">
        <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-10 mb-16 items-end">
            <div className="col-span-12 lg:col-span-7">
              <Reveal><span className="eyebrow">Honest answers</span></Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[1.02]">
                  A few <em className="italic text-lak-sage">good questions.</em>
                </h2>
              </Reveal>
            </div>
          </div>
          <div className="border-y border-black/10">
            {faqs.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.06}>
                <div className="grid grid-cols-12 gap-6 py-10 border-b border-black/10 last:border-b-0">
                  <div className="col-span-12 lg:col-span-5">
                    <h3 className="font-serif text-2xl lg:text-3xl font-light tracking-tight">{f.q}</h3>
                  </div>
                  <div className="col-span-12 lg:col-span-7">
                    <p className="text-lak-soft leading-relaxed text-lg">{f.a}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
